import React, { useEffect } from 'react';

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';

import { withStyles } from '@material-ui/core';

import type { HrisImportEmployeeChangeLog } from '../../typescript-types/hris-import-employee-change-log';

import { loadHrisImportEmployeeChangeLogs } from '../../hris-import-employee-change-log/actions/load-hris-import-employee-change-logs';
import {
  hrisImportEmployeeChangeLogsForHrisImportLogIdAndOrgIdSelector,
  hrisImportEmployeeChangeLogsStateForHrisImportLogIdSelector,
} from '../hris-import-log-selectors';

import { employeeRoute } from '../../web-routes';

import { HrisEmployeeDiffTableChangesCell } from './hris-employee-diff-table-changes-cell';

import { DOMAIN } from '../../config/config';
import { humanize } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

// Picking which toolbar items to show specifically instead of everything included
// in GridToolbar
const CustomGridToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
  </GridToolbarContainer>
);

type HrisEmployeeDiffTableProps = {
  hrisImportLogId: number;
  filterForOrgId: number | null;
};

// The id is a field needed for DataGrid, we use the employeeChangeLogId for the id
export type HrisImportEmployeeChangeLogRow = HrisImportEmployeeChangeLog & {
  id: number;
};

// We needed this so that the height of the rows will expand with the content
// this allows us to expand the fields changed column and have it take up more space
const NoMaxHeightDataGrid = withStyles({
  root: {
    '& .MuiDataGrid-renderingZone': {
      maxHeight: 'none !important',
      // This creates a scroll bar that allows us to move along the rendering zone which overflows
      // when opening the collapsible 'fields changed' sections
      overflowY: 'scroll',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      maxHeight: 'none !important',
      whiteSpace: 'normal',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiDataGrid-row': {
      maxHeight: 'none !important',
    },
  },
})(DataGrid);

export const HrisEmployeeDiffTable = ({
  hrisImportLogId,
  filterForOrgId,
}: HrisEmployeeDiffTableProps) => {
  const hrisImportEmployeeChangeLogs =
    useSelector((state: any) =>
      hrisImportEmployeeChangeLogsForHrisImportLogIdAndOrgIdSelector(
        state,
        hrisImportLogId,
        filterForOrgId,
      ),
    ) ?? [];

  const filteredHrisImportEmployeeChangeLogs = filterForOrgId
    ? hrisImportEmployeeChangeLogs.filter((log) => log.orgId === filterForOrgId)
    : hrisImportEmployeeChangeLogs;

  const { loading } =
    useSelector((state: any) =>
      hrisImportEmployeeChangeLogsStateForHrisImportLogIdSelector(state, hrisImportLogId),
    ) ?? {};

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadHrisImportEmployeeChangeLogs(hrisImportLogId));
  }, [dispatch, hrisImportLogId]);

  const dataGridRows: HrisImportEmployeeChangeLogRow[] = filteredHrisImportEmployeeChangeLogs.map(
    ({ employeeId, employeeChangeLogId, ...rest }) => ({
      id: employeeChangeLogId,
      employeeChangeLogId,
      employeeId,
      ...rest,
    }),
  );

  const columns: GridColDef[] = [
    {
      // We set the id field as the employeeChangeLogId earlier
      field: 'id',
      headerName: ' ',
      width: 50,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const { orgId, employeeId } = params.row as HrisImportEmployeeChangeLogRow;
        return (
          <a
            href={`${DOMAIN}${employeeRoute(orgId, employeeId)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="u-verticalAlign-middle u-margin-sm"
            aria-label="Open"
          >
            <MaterialDesignIcon
              iconName={ICON_NAMES.OPEN_IN_NEW}
              className="u-verticalAlign-middle"
            />
          </a>
        );
      },
    },
    { field: 'firstName', headerName: 'First Name', flex: 0.3, disableColumnMenu: true },
    { field: 'lastName', headerName: 'Last Name', flex: 0.3, disableColumnMenu: true },
    {
      field: 'changeType',
      headerName: 'Type',
      width: 150,
      disableColumnMenu: true,
      valueFormatter: ({ value }: GridValueFormatterParams) => humanize(value as string),
    },
    {
      field: 'employeeChanges',
      headerName: 'Fields Changed',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => JSON.stringify(value),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as HrisImportEmployeeChangeLogRow;
        return <HrisEmployeeDiffTableChangesCell hrisImportEmployeeChangeLog={row} />;
      },
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <div className="u-height-500px u-width-100">
      <div className="u-display-flex u-height-100">
        <div className="u-flexGrow-1">
          <NoMaxHeightDataGrid
            loading={loading}
            rows={dataGridRows}
            columns={columns}
            components={{ Toolbar: CustomGridToolbar }}
            rowsPerPageOptions={[10, 15, 25]}
            autoPageSize
            isRowSelectable={() => false}
          />
        </div>
      </div>
    </div>
  );
};
